import { Container, Sprite, Text, Ticker } from "pixi.js";
import type { HeroController } from "./HeroController";
import { SpineLayout } from "@pixi/spine-layout";
import { heroesData } from "../settings/heroesData";
import { sounds } from "@/utils/Sounds";

const PANEL_TILT_ANGLE = -Math.PI / 90;
const PANEL_HERO_OFFSET_X = 100;
const PANEL_HERO_OFFSET_Y = -250;
const HERO_VALUES_OFFSET_Y = 5;
const HERO_NAME_FONT_SIZE = 34;
const HERO_NAME_STROKE_WIDTH = 4;
const HERO_VALUES_FONT_SIZE = 24;
const HERO_VALUES_STROKE_WIDTH = 1;
const HERO_VALUES_LINE_HEIGHT = 28;
const SELECTION_FADE_SPEED = 0.06;
const PANEL_MOVE_SPEED = 40;
const MOBILE_PARADE_TRACK_INDEX = 1;
const MOBILE_PARADE_HERO_SPACING = 400;
const MOBILE_PARADE_SCROLL_SPEED = 1;

export class ParadeController {
    private active = false;
    private isMobileMode = false;
    private fadeTicker = new Ticker();
    private fadeTargets = new Map<HeroController, number>();
    private panelTicker = new Ticker();
    private panelTargetX = 0;
    private panelTargetY = 0;
    private mobileParadeContainer = new Container();
    private mobileParadeTicker = new Ticker();
    private mobileHeroes: HeroController[] = [];
    private heroName: Text;
    private heroValues: Text;
    private heroValuesBG: Sprite;

    constructor(
        private layout: SpineLayout,
        private heroes: Map<string, HeroController>
    ) {
        this.fadeTicker.add(this.fade, this);
        this.panelTicker.add(this.movePanel, this);
        this.mobileParadeTicker.add(this.scrollMobileParade, this);
        this.heroName = new Text({
            style: {
                fontSize: HERO_NAME_FONT_SIZE,
                fill: '#000000',
                align: 'center',
                fontFamily: 'Arial',
                stroke: {
                    color: '#ffffff',
                    width: HERO_NAME_STROKE_WIDTH,
                    pixelLine: true
                }
            }
        });

        this.heroValues = new Text({
            style: {
                fontSize: HERO_VALUES_FONT_SIZE,
                fill: '#000000',
                fontFamily: 'Arial',
                stroke: {
                    color: '#ffffff',
                    width: HERO_VALUES_STROKE_WIDTH,
                    pixelLine: true
                },
                lineHeight: HERO_VALUES_LINE_HEIGHT
            }
        });

        this.heroValues.y = HERO_VALUES_OFFSET_Y;

        this.heroValuesBG = Sprite.from('attributes');
        this.heroValuesBG.addChild(this.heroValues);

        this.heroName.rotation = PANEL_TILT_ANGLE;
        this.heroValuesBG.rotation = PANEL_TILT_ANGLE;

        this.heroName.anchor.set(0.5, 1);
        this.heroValues.anchor.set(0.5, 0);
        this.heroValuesBG.anchor.set(0.5, 0);

        this.layout.addChild(this.heroName, this.heroValuesBG);

        this.hideHeroData();
    }

    startMobile(trackContainers: Container[], heroes: HeroController[]) {
        const topTrack = trackContainers[MOBILE_PARADE_TRACK_INDEX];

        if (!topTrack) {
            console.warn(`Top track container ${MOBILE_PARADE_TRACK_INDEX} does not exist.`);
            return;
        }

        this.active = true;
        this.isMobileMode = true;
        this.mobileHeroes = heroes;
        this.fadeTargets.clear();
        this.fadeTicker.stop();
        this.panelTicker.stop();

        this.mobileParadeContainer.removeChildren();
        this.mobileParadeContainer.x = 0;
        this.mobileParadeContainer.y = 0;
        topTrack.addChild(this.mobileParadeContainer);

        heroes.forEach((hero, i) => {
            this.mobileParadeContainer.addChild(hero);
            hero.x = -i * MOBILE_PARADE_HERO_SPACING;
            hero.alpha = 1;
            hero.visible = true;
            hero.state = 'run';
        });

        this.mobileParadeTicker.start();
    }

    private scrollMobileParade(ticker: Ticker) {
        this.mobileParadeContainer.x += MOBILE_PARADE_SCROLL_SPEED * ticker.deltaTime;
    }

    start(trackContainers: Container[], heroes: HeroController[]) {
        this.active = true;
        this.isMobileMode = false;
        this.fadeTargets.clear();
        this.fadeTicker.stop();

        heroes.forEach((hero, i) => {
            const trackContainer = trackContainers[i];

            if (!trackContainer) {
                console.warn(`Track container ${i} does not exist.`);
                return;
            }

            trackContainer.addChild(hero);
            hero.x = 0;
            hero.alpha = 1;
            hero.visible = true;
            hero.state = 'idle';
        });
    }

    stop() {
        this.active = false;
        this.isMobileMode = false;
        this.fadeTicker.stop();
        this.panelTicker.stop();
        this.mobileParadeTicker.stop();
        this.fadeTargets.clear();
        this.mobileParadeContainer.removeChildren();
        this.mobileParadeContainer.removeFromParent();
        this.mobileHeroes = [];
        this.hideHeroData();

        this.heroes.forEach((hero) => {
            hero.visible = true;
            hero.alpha = 1;
        });
    }

    selectHero(heroID?: string) {
        if (!this.active) return;

        if (this.isMobileMode) {
            this.selectHeroMobile(heroID);
            return;
        }

        if (!heroID) {
            this.heroes.forEach((hero) => this.fadeTo(hero, 1));
            this.hideHeroData();
            return;
        }

        const hero = this.heroes.get(heroID);

        if (!hero) {
            console.warn(`Hero with ID ${heroID} does not exist.`);
            return;
        }

        this.heroes.forEach((h) => {
            this.fadeTo(h, h.id === heroID ? 1 : 0);
        });

        const wasVisible = this.heroName.visible;

        hero.color = 'green';
        hero.playSelectedAnimation();
        sounds.playFX(`sounds/words/${hero.name}/select`);
        this.updateHeroData(heroID);
        this.positionPanelAt(hero, wasVisible);
    }

    private selectHeroMobile(heroID?: string) {
        if (!heroID) {
            this.mobileHeroes.forEach((hero, i) => {
                hero.visible = true;
                hero.alpha = 1;
                hero.state = 'run';
                hero.x = -i * MOBILE_PARADE_HERO_SPACING;
            });
            this.mobileParadeContainer.x = 0;
            this.hideHeroData();
            this.mobileParadeTicker.start();
            return;
        }

        const hero = this.heroes.get(heroID);

        if (!hero) {
            console.warn(`Hero with ID ${heroID} does not exist.`);
            return;
        }

        this.mobileParadeTicker.stop();

        this.mobileHeroes.forEach((h) => {
            if (h.id === heroID) {
                h.visible = true;
                h.alpha = 1;
                h.x = 0;
            } else {
                h.visible = false;
            }
        });

        this.mobileParadeContainer.x = 0;

        const wasVisible = this.heroName.visible;

        hero.color = 'green';
        sounds.playFX(`sounds/words/${hero.name}/select`);
        this.updateHeroData(heroID);
        this.positionPanelAt(hero, wasVisible);
    }

    private fadeTo(hero: HeroController, target: number) {
        if (target > 0) {
            hero.visible = true;
        }

        if (hero.alpha === target) {
            this.fadeTargets.delete(hero);
            if (target === 0) hero.visible = false;
            return;
        }

        this.fadeTargets.set(hero, target);
        this.fadeTicker.start();
    }

    private fade(ticker: Ticker) {
        if (this.fadeTargets.size === 0) {
            this.fadeTicker.stop();
            return;
        }

        const step = SELECTION_FADE_SPEED * ticker.deltaTime;

        this.fadeTargets.forEach((target, hero) => {
            if (hero.alpha < target) {
                hero.alpha = Math.min(target, hero.alpha + step);
            } else if (hero.alpha > target) {
                hero.alpha = Math.max(target, hero.alpha - step);
            }

            if (hero.alpha === target) {
                if (target === 0) hero.visible = false;
                this.fadeTargets.delete(hero);
            }
        });

        if (this.fadeTargets.size === 0) {
            this.fadeTicker.stop();
        }
    }

    private positionPanelAt(hero: HeroController, animate = true) {
        const global = hero.getGlobalPosition();
        const local = this.layout.toLocal(global);

        this.panelTargetX = local.x + PANEL_HERO_OFFSET_X;
        this.panelTargetY = local.y + PANEL_HERO_OFFSET_Y;

        if (animate) {
            this.panelTicker.start();
        } else {
            this.panelTicker.stop();
            this.heroName.position.set(this.panelTargetX, this.panelTargetY);
            this.heroValuesBG.position.set(this.panelTargetX, this.panelTargetY);
        }
    }

    private movePanel(ticker: Ticker) {
        const dx = this.panelTargetX - this.heroName.x;
        const dy = this.panelTargetY - this.heroName.y;
        const dist = Math.hypot(dx, dy);
        const step = PANEL_MOVE_SPEED * ticker.deltaTime;

        if (dist <= step) {
            this.heroName.position.set(this.panelTargetX, this.panelTargetY);
            this.heroValuesBG.position.set(this.panelTargetX, this.panelTargetY);
            this.panelTicker.stop();
            return;
        }

        const ratio = step / dist;
        const newX = this.heroName.x + dx * ratio;
        const newY = this.heroName.y + dy * ratio;

        this.heroName.position.set(newX, newY);
        this.heroValuesBG.position.set(newX, newY);
    }

    private updateHeroData(heroID: string) {
        const data = heroesData[heroID];

        if (data) {
            this.heroName.text = data.name;
            this.heroValues.text = `Speed: ${data.speed}\nCunning: ${data.cunning}\nLuck: ${data.luck}`;
        }

        this.showHeroData();
    }

    private showHeroData() {
        this.heroName.visible = true;
        this.heroValues.visible = true;
        this.heroValuesBG.visible = true;
    }

    private hideHeroData() {
        this.heroName.visible = false;
        this.heroValues.visible = false;
        this.heroValuesBG.visible = false;
    }
}
